import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { API_COMMON_CACHE_TIME, HELPER_OPTION_MAX_ROWS } from '@/utils/consts'
import { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { getShipperListV2 } from '@/services/shipper/apis'
import { getBrandNameOptions, getGoodsCodeOptions, getInventoryPriceStatuses } from './apis'
import { GetBrandNameOptionsReq, GetGoodsCodeOptionsReq, GetInventoryPriceStatusesReq } from './types'

const PATH = ['inventory', 'inventoryPriceStatus'] as const

export const inventoryPriceStatusQueryKeys = {
  all: PATH,
  list: (params: GetInventoryPriceStatusesReq) => [...PATH, 'LIST', params] as const,
  shippers: [...PATH, 'SHIPPERS'] as const,
  brandNames: (params: GetBrandNameOptionsReq) => [...PATH, 'BRAND_NAMES', params] as const,
  goodsCodes: (params: GetGoodsCodeOptionsReq) => [...PATH, 'GOODS_CODES', params] as const,
}

/**
 * ST11 재고현황(단가) 목록.
 *
 * 진입 즉시 조회하지 않는다(`enabled: false`) — 조회 버튼으로만 실행한다.
 * 무필터 조회는 BE 가 400 으로 막는 경로라 자동 조회를 걸면 진입 때마다 실패한다. (ST02 와 동일 근거)
 * 실패(HTTP 400)는 reject 로 오므로 호출부가 refetch 반환값의 error 에서 message 를 꺼내 알럿한다.
 */
export const useGetInventoryPriceStatuses = (params: GetInventoryPriceStatusesReq) => {
  const { data, ...rest } = useQuery({
    queryKey: inventoryPriceStatusQueryKeys.list(params),
    queryFn: async () => {
      const res = await getInventoryPriceStatuses(params)
      return res.data?.items ?? []
    },
    enabled: false,
  })

  return { items: data ?? [], ...rest }
}

/**
 * 화주명 셀렉트 옵션 — `POST /api/v1/get-shippers` 전체 목록(코드/명).
 *
 * ST07 필터 `sprNm` 은 화주**코드**(oi.SPR_NM EQUAL)라 value=코드, label=화주명으로 만든다(ST10 과 동일 지점).
 * 그리드 화주명 컬럼(코드→명 표시)도 이 목록을 재사용한다 — 핸드오프 §컬럼 노트 "코드→명 매핑".
 */
export const useGetShipperOptions = () => {
  const { data, ...rest } = useQuery({
    queryKey: inventoryPriceStatusQueryKeys.shippers,
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      // 클라이언트 검색(showSearch) 전제로 전건을 한 번에 받는다(get-shipper-names 의 size=10000 선례).
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })
      return (res?.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { options: data ?? [], ...rest }
}

/**
 * 상품코드 서치헬퍼 목록. 조회 조건(화주·브랜드명·상품명)별로 캐시한다.
 * 모달이 닫혀 있는 동안 미리 조회하지 않도록 `enabled`(모달 open)로 게이트한다.
 */
export const useGetGoodsCodeOptions = (params: GetGoodsCodeOptionsReq, enabled = true) => {
  const { data, ...rest } = useQuery({
    queryKey: inventoryPriceStatusQueryKeys.goodsCodes(params),
    queryFn: async () => {
      const res = await getGoodsCodeOptions(params)
      return res.data?.items ?? []
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data ?? [], ...rest }
}

interface BrandNameOptionsData {
  items: string[]
  /** 응답이 BE 상한(1,000행)에 닿았다 — 화면이 "조건을 좁혀 달라" 안내를 띄운다. */
  isTruncated: boolean
}

/**
 * 브랜드명 서치헬퍼 목록. 조회 조건(화주·브랜드명)별로 캐시한다.
 * 모달이 닫혀 있는 동안 미리 조회하지 않도록 `enabled`(모달 open)로 게이트한다.
 *
 * 브랜드명이 NULL 인 상품도 distinct 1행으로 오므로 거른다. `isTruncated` 는 응답이 BE 상한(1,000행)에
 * 닿았다는 뜻이며 거르기 전 건수로 판정한다.
 */
export const useGetBrandNameOptions = (
  params: GetBrandNameOptionsReq,
  enabled = true,
): Omit<UseQueryResult<BrandNameOptionsData, Error>, 'data'> & BrandNameOptionsData => {
  const { data, ...rest } = useQuery({
    queryKey: inventoryPriceStatusQueryKeys.brandNames(params),
    queryFn: async (): Promise<BrandNameOptionsData> => {
      const rows = (await getBrandNameOptions(params)).data?.items ?? []
      return { items: rows.map((row) => row.brandName ?? '').filter(Boolean), isTruncated: rows.length >= HELPER_OPTION_MAX_ROWS }
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data?.items ?? [], isTruncated: data?.isTruncated ?? false, ...rest }
}
